import { useMemo, useRef, useEffect } from 'react';
import { useMatch } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-match/index.tsx';
import useServiceProjectFeatures from '@atlassian/jira-servicedesk-features-store/src/index.tsx';
import { ENABLED } from '@atlassian/jira-servicedesk-common/src/model/service-project-features.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCurrentQueue } from '@atlassian/jira-router-resources-service-desk-queues/src/controllers/current-queue/index.tsx';
import { toItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/main.tsx';
import { getQueueFragment } from '@atlassian/jira-servicedesk-navigation/src/utils/queues/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	getBoardUrl,
	getJSMBoardUrl,
	getJSMCalendarUrl,
	getJSMListUrl,
	getUserBoardUrl,
} from '../../common/url-utils/index.tsx';
import { useBoardAndLocationContext } from '../use-board-and-location-context/index.tsx';
import { useIsJSMBoard } from '../../services/use-is-jsm-board/index.tsx';

const useRouterLocation = createRouterSelector((state) => state.location);

export function useFromUrlParams() {
	const location = useRouterLocation();
	return useMemo(
		() => ({
			fromCalendar: location.search.includes('from-calendar=1'),
			fromList: location.search.includes('from-list=1'),
		}),
		[location.search],
	);
}

export function useBoardUrl(isCMPBoard?: boolean): string {
	const lastValidBoardUrl = useRef<string | null>(null);
	const match = useMatch();
	const { project, user } = useBoardAndLocationContext();
	const isJSMBoard = useIsJSMBoard();
	const { queue } = useCurrentQueue();
	const isReallyCMPBoard = isCMPBoard !== undefined ? isCMPBoard : !project?.simplified;
	const projectKey = match.params.projectKey ?? '';
	const boardId = match.params.boardId ?? '';
	const category = toItsmPractice(match.params.practiceType);
	const { data: jsmFeaturesData } = useServiceProjectFeatures();
	const isBoardEnabled = isJSMBoard ? jsmFeaturesData?.['jsm-board']?.status === ENABLED : true;

	const { fromCalendar, fromList } = useFromUrlParams();

	const boardUrl = useMemo(() => {
		if (!projectKey && !boardId) {
			if (lastValidBoardUrl.current && fg('fix_use_board_url_for_swimlanes')) {
				return lastValidBoardUrl.current;
			}
			throw new Error('useBoardUrl() can only be used with board URLs');
		}

		if (user) {
			return getUserBoardUrl(user.accountId, boardId);
		}

		if (queue && fg('jsm_views_inside_queues_-_main_flag')) {
			return `/jira/servicedesk/projects/${projectKey}/${getQueueFragment(queue.id, category)}/board/${boardId}`;
		}

		if (isJSMBoard) {
			if (fromCalendar && expVal('jsm_multi_project_list_view_milestone_1', 'isEnabled', false)) {
				return getJSMCalendarUrl(projectKey, boardId);
			}
			if (fromList && expVal('jsm_multi_project_list_view_milestone_1', 'isEnabled', false)) {
				return getJSMListUrl(projectKey, boardId);
			}
			return isBoardEnabled
				? getJSMBoardUrl(projectKey, boardId)
				: getJSMCalendarUrl(projectKey, boardId);
		}

		return getBoardUrl(projectKey, boardId, isReallyCMPBoard);
	}, [
		boardId,
		isJSMBoard,
		isReallyCMPBoard,
		projectKey,
		user,
		isBoardEnabled,
		queue,
		category,
		fromCalendar,
		fromList,
	]);

	// this is for the case when a component that calls `useBoardUrl` unmounts after the url changes.
	// instead of throwing an error, just use the last valid board url.
	useEffect(() => {
		if (boardUrl && fg('fix_use_board_url_for_swimlanes')) {
			lastValidBoardUrl.current = boardUrl;
		}
	}, [boardUrl]);

	return boardUrl;
}
